'use client';

import { useEffect, useRef } from 'react';
import type { PromoCodeState } from '@/lib/cart-persistence';

function promoEquals(a: PromoCodeState | null, b: PromoCodeState | null): boolean {
  if (a == null && b == null) return true;
  if (!a || !b) return false;
  const sa = a.source ?? 'manual';
  const sb = b.source ?? 'manual';
  return (
    a.code === b.code &&
    a.promoCodeId === b.promoCodeId &&
    sa === sb &&
    Math.abs((a.discountAmount || 0) - (b.discountAmount || 0)) < 0.005
  );
}

type BestApi = {
  success?: boolean;
  best?: { id: string; code: string; discountAmount: number } | null;
};

/**
 * Re-validates the applied promo when the basket or fulfillment mode changes, then
 * auto-selects the best eligible code when it beats the current discount (issue #61).
 */
export function usePromoCartOptimization(opts: {
  apiBase: string;
  orderAmount: number;
  fulfillmentMode: 'pickup' | 'delivery';
  userId?: string | null;
  cartHasItems: boolean;
  promoCode: PromoCodeState | null;
  setPromoCode: (p: PromoCodeState | null) => void;
  onPromoInvalidated?: (message: string) => void;
  /** Clears inline promo errors when a valid or better code is applied. */
  onPromoResolved?: () => void;
}): void {
  const {
    apiBase,
    orderAmount,
    fulfillmentMode,
    userId,
    cartHasItems,
    promoCode,
    setPromoCode,
    onPromoInvalidated,
    onPromoResolved,
  } = opts;

  const promoRef = useRef(promoCode);
  promoRef.current = promoCode;

  const onInvalidRef = useRef(onPromoInvalidated);
  onInvalidRef.current = onPromoInvalidated;
  const onResolvedRef = useRef(onPromoResolved);
  onResolvedRef.current = onPromoResolved;

  useEffect(() => {
    if (!cartHasItems) return;

    const t = window.setTimeout(() => {
      void (async () => {
        let next: PromoCodeState | null = promoRef.current;
        let invalidated: string | null = null;

        if (next?.code) {
          try {
            const res = await fetch(`${apiBase}/api/promo/validate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                code: next.code,
                orderAmount,
                fulfillmentMode,
                userId: userId || undefined,
              }),
            });
            const data = await res.json();
            if (!data?.success) {
              invalidated = typeof data?.error === 'string' ? data.error : 'Your promo code is no longer valid for this cart.';
              next = null;
            } else if (data.promoCode) {
              const prevSource = promoRef.current?.source ?? 'manual';
              next = {
                code: data.promoCode.code ?? next.code,
                promoCodeId: data.promoCode.id,
                discountAmount: Number(data.promoCode.discountAmount) || 0,
                source: prevSource === 'auto' ? 'auto' : 'manual',
              };
            }
          } catch {
            /* transient network, keep current promo */
          }
        }

        try {
          const res = await fetch(`${apiBase}/api/promo/best`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderAmount,
              fulfillmentMode,
              userId: userId || undefined,
            }),
          });
          const data = (await res.json()) as BestApi;
          const b = data.success && data.best && data.best.discountAmount > 0 ? data.best : null;
          if (b) {
            const cur = next?.discountAmount ?? 0;
            if (b.discountAmount > cur + 0.001) {
              next = {
                code: b.code,
                promoCodeId: b.id,
                discountAmount: b.discountAmount,
                source: 'auto',
              };
            }
          }
        } catch {
          /* ignore */
        }

        if (!promoEquals(next, promoRef.current)) {
          setPromoCode(next);
          if (next) {
            onResolvedRef.current?.();
          }
        }

        if (invalidated && !next) {
          onInvalidRef.current?.(invalidated);
        }
      })();
    }, 550);

    return () => window.clearTimeout(t);
  }, [
    apiBase,
    orderAmount,
    fulfillmentMode,
    userId,
    cartHasItems,
    promoCode?.code,
    promoCode?.promoCodeId,
    setPromoCode,
  ]);
}
